import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Quote, MessageSquarePlus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={15} className={i <= n ? "fill-blaze-orange text-blaze-orange" : "text-white/20"} />
      ))}
    </div>
  );
}

export function TestimonialsSection() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/testimonials")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="testimonials" className="relative py-20 sm:py-28 bg-ink-800">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-blaze-orange">Blazer Families</span>
          <h2 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl text-white leading-none">
            What People <span className="text-gradient-blaze">Say</span>
          </h2>
        </Reveal>

        {loaded && items.length === 0 ? (
          <Reveal>
            <div
              data-testid="testimonials-empty"
              className="mt-10 rounded-2xl border border-dashed border-white/15 bg-ink-700/50 p-10 text-center"
            >
              <Quote className="mx-auto text-blaze-orange" size={40} />
              <p className="mt-4 text-lg text-white/70">No testimonials yet — be the first to share your Blazer story.</p>
              <Button
                asChild
                data-testid="testimonials-share-empty"
                className="mt-6 bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full px-7 h-11"
              >
                <Link to="/testimonials">
                  <MessageSquarePlus size={17} className="mr-1.5" /> Share Your Experience
                </Link>
              </Button>
            </div>
          </Reveal>
        ) : (
          <>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.slice(0, 6).map((t, i) => (
                <Reveal key={t.id} delay={(i % 3) * 0.08}>
                  <figure
                    data-testid={`testimonial-${i}`}
                    className="hover-lift h-full rounded-2xl border border-white/10 bg-ink-700 p-6 hover:border-blaze-orange/40"
                  >
                    <Quote className="text-blaze-blueLight" size={28} />
                    <blockquote className="mt-3 text-white/80 leading-relaxed">"{t.message}"</blockquote>
                    <figcaption className="mt-5 flex items-center justify-between">
                      <div>
                        <p className="font-heading text-xl tracking-wide text-white leading-none">{t.name}</p>
                        {t.role && <p className="text-xs text-white/50 mt-1">{t.role}</p>}
                      </div>
                      <Stars n={t.rating} />
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div className="mt-10 flex justify-center">
                <Button
                  asChild
                  data-testid="testimonials-share"
                  className="bg-blaze-blue hover:bg-blaze-blueLight text-white font-bold uppercase tracking-wide rounded-full px-7 h-11"
                >
                  <Link to="/testimonials">
                    Share Your Experience <ArrowRight size={16} className="ml-1" />
                  </Link>
                </Button>
              </div>
            </Reveal>
          </>
        )}
      </div>
    </section>
  );
}
